import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';

import * as Sentry from '@sentry/react';

import { isErrorLogEnable } from '@utils/helpers';

import App from './App';

if (import.meta.env.VITE_APP_ENVIRONMENT === 'development') {
  void import('./dev/axe')
    .then(({ runRDA11yAudit }) => {
      (window as unknown as Record<string, unknown>).runRDA11yAudit = runRDA11yAudit;
      console.log(
        '%c[Accessibility Audit] %cType %crunRDA11yAudit()%c in console to run audit',
        'color: #4caf50; font-weight: bold;',
        'color: #fff;',
        'color: #f57c00; font-weight: bold;',
        'color: #fff;',
      );
    })
    .catch(() => undefined);
}

const ignoreMessages = [
  "Error: Failed to complete negotiation with the server: Error: : Status code '401'",
  'Error: Server timeout elapsed without receiving a message from the server',
  'Error: WebSocket closed with status code: 1006 (no reason given)',
  'AxiosQueryError: 400: Validation error.',
  'SyntaxError: JSON Parse error: Unexpected identifier "CompanyRequiresMfa"',
  'The connection could not be found on the server, either the endpoint may not be a SignalR endpoint',
];

Sentry.init({
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_ENVIRONMENT,
  release: import.meta.env.VITE_APP_VERSION,
  enabled: isErrorLogEnable,
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration({
      networkDetailAllowUrls: [import.meta.env.VITE_APP_API, import.meta.env.VITE_APP_IDENTITY],
      networkRequestHeaders: ['Authorization'],
      networkResponseHeaders: ['WWW-Authenticate'],
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
    }),
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.browserTracingIntegration(),
  ],
  tracesSampleRate: parseFloat(import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE || '1.0'),
  tracePropagationTargets: [import.meta.env.VITE_APP_API, import.meta.env.VITE_APP_IDENTITY],
  replaysOnErrorSampleRate: 1.0,
  ignoreErrors: ignoreMessages,
  normalizeDepth: 10,
  normalizeMaxBreadth: 10000,
  maxValueLength: 1000,
});

const root = createRoot(document.getElementById('root') as HTMLElement);

root.render(<App />);
